import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Modal, Tabs, Tab, Spinner, Collapse } from 'react-bootstrap';
import { FaPlay, FaStop, FaCode, FaDownload, FaEye, FaChevronDown, FaChevronUp, FaChevronRight, FaClock, FaRobot, FaFile, FaCheck, FaExclamationTriangle, FaEdit } from 'react-icons/fa';
import { API_URL } from '../config';

const WorkflowExecutionReview = ({ 
  executionData, 
  onFinalize, 
  onEditWorkflow, 
  onRetestWorkflow,
  workflowType 
}) => {
  const [selectedStep, setSelectedStep] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [showRawData, setShowRawData] = useState(false);
  const [filterLevel, setFilterLevel] = useState('all'); // 'all', 'important', 'errors'
  const [timelineView, setTimelineView] = useState('visual'); // 'visual', 'detailed', 'raw'

  // AI text descriptions for generated files, keyed by filename.
  // Generated progressively as files stream in during testing, so the tester
  // sees the same alt-text experience as the deployed tool.
  const [fileDescriptions, setFileDescriptions] = useState({});
  const requestedDescRef = useRef(new Set());

  useEffect(() => {
    const files = (executionData && executionData.generatedFiles) || [];
    files.forEach((file) => {
      const key = file && file.filename;
      if (!key || !file.data || requestedDescRef.current.has(key)) return;

      const isSvg = file.type === 'image/svg+xml';
      const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
      if (!isSvg && !isImage) return; // only describe visual outputs

      const payload = { filename: key };
      try {
        if (isSvg) {
          payload.svg_content = atob(file.data).substring(0, 8000);
        } else {
          payload.image_data = `data:${file.type};base64,${file.data}`;
        }
      } catch (e) {
        return; // malformed data, skip
      }

      requestedDescRef.current.add(key);
      setFileDescriptions((prev) => ({ ...prev, [key]: { status: 'loading', text: 'Generating description…' } }));

      fetch(`${API_URL}/api/describe-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((res) => {
          setFileDescriptions((prev) => ({
            ...prev,
            [key]: { status: 'done', text: res.description || 'No description available.' },
          }));
        })
        .catch((err) => {
          setFileDescriptions((prev) => ({
            ...prev,
            [key]: { status: 'error', text: `Could not generate description: ${err.message}` },
          }));
        });
    });
  }, [executionData]);

  // Helper functions - defined before they are used
  const extractAgentInfo = (result) => {
    if (result.type === 'subgraph_update') {
      return {
        name: result.graph_id || 'Unknown Agent',
        type: result.update?.update_type || 'processing',
        description: Object.entries(result.update?.meaningful_content || {})
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ')
      };
    }
    return {
      name: result.agent || 'System',
      type: 'update',
      description: result.message || 'Processing...'
    };
  };

  const extractMessages = (result) => {
    const messages = [];
    
    if (result.update?.meaningful_content) {
      Object.entries(result.update.meaningful_content).forEach(([key, value]) => {
        messages.push({
          type: 'info',
          content: `${key}: ${value}`,
          timestamp: result.timestamp
        });
      });
    }

    if (result.result?.error) {
      messages.push({
        type: 'error',
        content: result.result.error,
        timestamp: result.timestamp
      });
    }

    return messages;
  };

  const extractGeneratedFiles = (result) => {
    if (!executionData.generatedFiles) return [];
    
    // Filter files generated during this step (rough estimation based on timing)
    return executionData.generatedFiles.filter(file => 
      // Simple heuristic - files generated around the same time as this step
      true // For now, show all files - could be improved with better timing
    );
  };

  // Process execution data into structured steps
  const processedSteps = React.useMemo(() => {
    if (!executionData || !executionData.results) return [];

    // Handle manual workflow results differently than streaming results
    if (executionData.mode === 'manual') {
      const steps = [];
      
      executionData.results.forEach((result, index) => {
        if (result.success && result.result) {
          // Extract individual agent results from the manual workflow result
          const manualResult = result.result;
          
          // If the result has a 'results' field with individual agent outputs
          if (manualResult.results) {
            Object.entries(manualResult.results).forEach(([agentName, agentResult], agentIndex) => {
              steps.push({
                id: `agent_${agentIndex}`,
                timestamp: new Date().toISOString(),
                type: 'agent_result',
                status: 'success',
                duration: result.execution_time || 0,
                agent: {
                  name: agentName,
                  type: 'agent',
                  description: `Agent execution completed`
                },
                input: executionData.input,
                output: agentResult,
                error: null,
                messages: [{
                  type: 'info',
                  content: `Agent result: ${JSON.stringify(agentResult, null, 2)}`,
                  timestamp: new Date().toISOString()
                }],
                generatedFiles: [],
                rawData: agentResult
              });
            });
          }
          
          // If the result has final_data with individual agent outputs
          if (manualResult.final_data) {
            Object.entries(manualResult.final_data).forEach(([key, value], dataIndex) => {
              if (key.endsWith('_result')) {
                const agentName = key.replace('_result', '');
                steps.push({
                  id: `output_${dataIndex}`,
                  timestamp: new Date().toISOString(),
                  type: 'agent_output',
                  status: 'success', 
                  duration: result.execution_time || 0,
                  agent: {
                    name: agentName,
                    type: 'output',
                    description: `Agent output`
                  },
                  input: executionData.input,
                  output: value,
                  error: null,
                  messages: [{
                    type: 'info',
                    content: `Output: ${JSON.stringify(value, null, 2)}`,
                    timestamp: new Date().toISOString()
                  }],
                  generatedFiles: [],
                  rawData: value
                });
              }
            });
          }
          
          // Add overall execution summary
          steps.push({
            id: `summary_${index}`,
            timestamp: new Date().toISOString(),
            type: 'execution_summary',
            status: 'success',
            duration: result.execution_time || 0,
            agent: {
              name: 'Workflow Summary',
              type: 'summary',
              description: `Manual workflow completed successfully`
            },
            input: executionData.input,
            output: manualResult,
            error: null,
            messages: [{
              type: 'success',
              content: `Workflow completed in ${result.execution_time || 0}ms`,
              timestamp: new Date().toISOString()
            }],
            generatedFiles: manualResult.generated_files || [],
            rawData: manualResult
          });
        } else {
          // Handle error case
          steps.push({
            id: `error_${index}`,
            timestamp: new Date().toISOString(),
            type: 'error',
            status: 'error',
            duration: result.execution_time || 0,
            agent: {
              name: 'Workflow Error',
              type: 'error',
              description: 'Workflow execution failed'
            },
            input: executionData.input,
            output: null,
            error: result.error,
            messages: [{
              type: 'error',
              content: result.error || 'Unknown error occurred',
              timestamp: new Date().toISOString()
            }],
            generatedFiles: [],
            rawData: result
          });
        }
      });
      
      return steps;
    } else {
      // Handle streaming results (automatic workflows)
      return executionData.results.map((result, index) => {
        const stepData = {
          id: index,
          timestamp: result.timestamp || new Date().toISOString(),
          type: result.type,
          status: result.type === 'complete' ? 'success' : (result.type === 'error' ? 'error' : 'running'),
          duration: result.duration || 0,
          agent: extractAgentInfo(result),
          input: result.input || executionData.input,
          output: result.output || result.update?.meaningful_content,
          error: result.error,
          messages: extractMessages(result),
          generatedFiles: extractGeneratedFiles(result),
          rawData: result
        };

        return stepData;
      });
    }
  }, [executionData]);

  const toggleStepExpansion = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const getStepIcon = (step) => {
    switch (step.status) {
      case 'success':
        return <FaCheck className="text-success" />;
      case 'error':
        return <FaExclamationTriangle className="text-danger" />;
      case 'running':
        return <Spinner animation="border" size="sm" />;
      default:
        return <FaClock className="text-muted" />;
    }
  };

  const getStepVariant = (step) => {
    switch (step.status) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      case 'running':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const filteredSteps = processedSteps.filter(step => {
    switch (filterLevel) {
      case 'important':
        return step.status === 'success' || step.status === 'error' || step.generatedFiles.length > 0;
      case 'errors':
        return step.status === 'error';
      default:
        return true;
    }
  });

  const executionSummary = React.useMemo(() => {
    const total = processedSteps.length;
    const successful = processedSteps.filter(s => s.status === 'success').length;
    const errors = processedSteps.filter(s => s.status === 'error').length;
    const totalDuration = processedSteps.reduce((sum, s) => sum + s.duration, 0);
    const filesGenerated = executionData.generatedFiles?.length || 0;

    return { total, successful, errors, totalDuration, filesGenerated };
  }, [processedSteps, executionData]);

  const renderStepDetails = (step) => (
    <div className="step-details">
      <Row>
        <Col md={6}>
          <h6>Input Data:</h6>
          <div className="code-block">
            <pre>{typeof step.input === 'string' ? step.input : JSON.stringify(step.input, null, 2)}</pre>
          </div>
          
          {step.messages.length > 0 && (
            <div className="mt-3">
              <h6>Messages:</h6>
              {step.messages.map((msg, idx) => (
                <Alert key={idx} variant={msg.type === 'error' ? 'danger' : 'info'} className="mb-2">
                  <small>{msg.content}</small>
                </Alert>
              ))}
            </div>
          )}
        </Col>
        
        <Col md={6}>
          {step.output && (
            <div>
              <h6>Output:</h6>
              {/* If output is a file path, render preview and download */}
              {typeof step.output === 'string' && step.output.match(/\.(svg|png|jpg|jpeg)$/i) ? (
                <div>
                  {step.output.toLowerCase().endsWith('.svg') ? (
                    <div className="mb-2">
                      <div className="code-block" style={{ background: '#fff' }}>
                        <object data={`/api/generated-files/${encodeURIComponent(step.output.split('/').pop())}`} type="image/svg+xml" style={{ width: '100%', minHeight: 200 }} />
                      </div>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <img src={`/api/generated-files/${encodeURIComponent(step.output.split('/').pop())}`} alt={step.output.split('/').pop()} style={{ maxWidth: '100%', maxHeight: 240 }} />
                    </div>
                  )}
                  <a className="btn btn-sm btn-outline-primary" href={`/api/generated-files/${encodeURIComponent(step.output.split('/').pop())}`} download>
                    Download
                  </a>
                </div>
              ) : (
                <div className="code-block">
                  <pre>{typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          
          {step.error && (
            <div className="mt-3">
              <h6>Error Details:</h6>
              <Alert variant="danger">
                {step.error}
              </Alert>
            </div>
          )}
          
          {step.generatedFiles.length > 0 && (
            <div className="mt-3">
              <h6>Generated Files:</h6>
              {step.generatedFiles.map((file, idx) => (
                <div key={idx} className="file-preview mb-2">
                  <div className="d-flex align-items-center">
                    <FaFile className="me-2" />
                    <span>{file.filename}</span>
                    <Button as="a" href={`/api/generated-files/${encodeURIComponent(file.filename)}`} download variant="outline-primary" size="sm" className="ms-auto">
                      <FaDownload />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );

  if (!executionData) {
    return (
      <div className="text-center p-4">
        <p className="text-muted">No execution data available. Run a workflow to see results.</p>
      </div>
    );
  }

  return (
    <div className="workflow-execution-review">
      <style>{`
        .workflow-execution-review {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .execution-summary {
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          border-radius: 10px;
          padding: 20px;
          margin-bottom: 20px;
        }
        
        .timeline-item {
          display: flex;
          margin-bottom: 0;
        }
        
        .timeline-marker {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-right: 20px;
        }
        
        .timeline-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          z-index: 1;
        }
        
        .timeline-line {
          width: 2px;
          background: #dee2e6;
          flex-grow: 1;
          margin-top: 10px;
        }
        
        .timeline-content {
          flex-grow: 1;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .code-block {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 5px;
          padding: 10px;
          max-height: 200px;
          overflow-y: auto;
        }
        
        .code-block pre {
          margin: 0;
          font-size: 0.875rem;
          white-space: pre-wrap;
        }
        
        .file-preview {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 5px;
          padding: 10px;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
        }
        
        .stat-card {
          background: white;
          border-radius: 8px;
          padding: 15px;
          text-align: center;
          border: 1px solid #dee2e6;
        }
        
        .stat-value {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .stat-label {
          color: #6c757d;
          font-size: 0.875rem;
        }
      `}</style>

      {/* Execution Summary */}
      <div className="execution-summary">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>
            <FaRobot className="me-2" />
            Workflow Execution Results
          </h4>
          <div>
            <Button variant="outline-primary" size="sm" className="me-2" onClick={onEditWorkflow}>
              <FaEdit className="me-1" />
              Edit Workflow
            </Button>
            <Button variant="outline-success" size="sm" className="me-2" onClick={onRetestWorkflow}>
              <FaPlay className="me-1" />
              Retest
            </Button>
            <Button variant="primary" size="sm" onClick={onFinalize}>
              <FaCheck className="me-1" />
              Finalize Workflow
            </Button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value text-primary">{executionSummary.total}</div>
            <div className="stat-label">Total Steps</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-success">{executionSummary.successful}</div>
            <div className="stat-label">Successful</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-danger">{executionSummary.errors}</div>
            <div className="stat-label">Errors</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-info">{Math.round(executionSummary.totalDuration)}ms</div>
            <div className="stat-label">Total Time</div>
          </div>
          <div className="stat-card">
            <div className="stat-value text-warning">{executionSummary.filesGenerated}</div>
            <div className="stat-label">Files Generated</div>
          </div>
        </div>
      </div>

      {/* Execution Timeline */}
      {processedSteps.length > 0 && (
        <Card className="mt-3">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5><FaClock className="me-2" />Execution Timeline ({processedSteps.length} steps)</h5>
              <div>
                <Button variant="outline-secondary" size="sm" className="me-2">
                  Show Details
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="timeline">
              {processedSteps.map((step, index) => (
                <div key={step.id} className="timeline-item">
                  <div className="timeline-marker">
                    <div className={`timeline-icon bg-${getStepVariant(step)}`}>
                      {getStepIcon(step)}
                    </div>
                    {index < processedSteps.length - 1 && <div className="timeline-line"></div>}
                  </div>
                  <div className="timeline-content">
                    <Card className="mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1">{step.agent.name}</h6>
                            <small className="text-muted">{step.agent.description}</small>
                          </div>
                          <Badge bg={getStepVariant(step)}>{step.status}</Badge>
                        </div>
                        
                        {step.output && (
                          <div className="mt-2">
                            <small><strong>Output:</strong></small>
                            <div className="text-muted small">
                              {typeof step.output === 'object' 
                                ? JSON.stringify(step.output, null, 2).substring(0, 200) + '...'
                                : step.output.toString().substring(0, 200) + (step.output.toString().length > 200 ? '...' : '')
                              }
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-2">
                          <Button 
                            variant="outline-info" 
                            size="sm"
                            onClick={() => toggleStepExpansion(step.id)}
                          >
                            {expandedSteps[step.id] ? <FaChevronUp /> : <FaChevronDown />}
                            {expandedSteps[step.id] ? ' Less' : ' More'}
                          </Button>
                        </div>
                        
                        <Collapse in={expandedSteps[step.id]}>
                          <div className="mt-3">
                            {renderStepDetails(step)}
                          </div>
                        </Collapse>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Generated Files Section */}
      {executionData.generatedFiles && executionData.generatedFiles.length > 0 && (
        <Card className="mt-3">
          <Card.Header>
            <h5><FaFile className="me-2" />Generated Files ({executionData.generatedFiles.length})</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              {executionData.generatedFiles.map((file, index) => (
                <Col key={index} md={6} lg={4} className="mb-3">
                  <div className="file-preview">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{file.filename}</strong>
                      <Button variant="outline-primary" size="sm">
                        <FaDownload />
                      </Button>
                    </div>
                    {file.type === 'image/svg+xml' && file.data && (
                      <div 
                        className="visual-content"
                        style={{ 
                          border: '1px solid #ddd', 
                          borderRadius: '5px',
                          padding: '10px', 
                          backgroundColor: '#fff',
                          maxHeight: '200px',
                          overflow: 'hidden'
                        }}
                        dangerouslySetInnerHTML={{ __html: atob(file.data) }}
                      />
                    )}
                    {file.type !== 'image/svg+xml' && file.data && (
                      <img 
                        className="visual-content"
                        src={`data:${file.type};base64,${file.data}`}
                        alt={fileDescriptions[file.filename]?.status === 'done' ? fileDescriptions[file.filename].text : file.filename}
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      />
                    )}
                    {fileDescriptions[file.filename] && (
                      <div className="mt-2">
                        <strong className="small d-block">Description:</strong>
                        <div
                          className={`text-desc small ${fileDescriptions[file.filename].status === 'loading' ? 'text-muted fst-italic' : ''}`}
                        >
                          {fileDescriptions[file.filename].status === 'loading' && (
                            <Spinner animation="border" size="sm" className="me-2" />
                          )}
                          {fileDescriptions[file.filename].text}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default WorkflowExecutionReview;
